const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Detect if we're running in react-snap pre-rendering environment
// During pre-render, skip API calls since we already have static data
export const isPrerendering = () => {
  if (typeof window === 'undefined') return true;
  if (typeof navigator !== 'undefined' && navigator.userAgent.includes('ReactSnap')) return true;
  if (window.__PRERENDER_INJECTED) return true;
  return false;
};

// Simple in-memory cache for API responses
// Prevents duplicate requests when multiple components mount simultaneously
const cache = {
  data: {},
  promises: {},
  ttl: 5 * 60 * 1000, // 5 minute cache TTL

  get(key) {
    const entry = this.data[key];
    if (entry && Date.now() - entry.timestamp < this.ttl) {
      return entry.value;
    }
    return null;
  },

  set(key, value) {
    this.data[key] = { value, timestamp: Date.now() };
  },

  // For deduplicating in-flight requests
  getPromise(key) {
    return this.promises[key];
  },

  setPromise(key, promise) {
    this.promises[key] = promise;
    promise.finally(() => {
      delete this.promises[key];
    });
    return promise;
  }
};

// Helper to wrap API calls with caching
const cachedFetch = async (key, fetchFn) => {
  // Skip API calls during pre-rendering (react-snap)
  // Static data is already available from useStaticData
  if (isPrerendering()) {
    return null;
  }

  // Check cache first
  const cached = cache.get(key);
  if (cached) return cached;

  // Check for in-flight request
  const existing = cache.getPromise(key);
  if (existing) return existing;

  // Make new request and cache the promise
  const promise = fetchFn().then(result => {
    cache.set(key, result);
    return result;
  });

  return cache.setPromise(key, promise);
};

// API service functions
export const apiService = {
  // Business Info
  async getBusinessInfo() {
    return cachedFetch('businessInfo', async () => {
      try {
        const response = await fetch(`${API}/business-info`);
        if (response.ok) {
          return await response.json();
        }
        throw new Error('Failed to fetch business info');
      } catch (error) {
        console.error('Error fetching business info:', error);
        // Fallback to default data.
        // phone            = Owaza office line (primary public contact)
        // direct_line_phone = Hanshi Jim Rivera's personal/direct line
        return {
          name: "Owaza Martial Arts",
          address: "14726 County Line Rd, Spring Hill, FL 34610",
          phone: "(352) 428-0086",
          direct_line_phone: "(352) 428-0086",
          direct_line_label: "Hanshi Jim Rivera's direct line",
          email: "info@owazamartialarts.com"
        };
      }
    });
  },

  // Programs
  async getPrograms() {
    return cachedFetch('programs', async () => {
      try {
        const response = await fetch(`${API}/programs`);
        if (response.ok) {
          return await response.json();
        }
        throw new Error('Failed to fetch programs');
      } catch (error) {
        console.error('Error fetching programs:', error);
        // Fallback to mock data
        return [
          {
            id: 1,
            title: "Kids (Ages 4-6)",
            description: "Fun, foundational martial arts for our youngest students.",
            age_range: "4-6 years",
            features: ["Basic techniques", "Coordination skills", "Fun games", "Character building"],
            image_url: "https://d598mmm7zleni.cloudfront.net/images/kids.jpg"
          },
          {
            id: 2,
            title: "Youth (Ages 7-12)",
            description: "Structured martial arts training that builds confidence.",
            age_range: "7-12 years",
            features: ["Self-defense", "Discipline", "Confidence", "Fitness"],
            image_url: "https://d598mmm7zleni.cloudfront.net/images/youth.jpg"
          },
          {
            id: 3,
            title: "Teens & Adults",
            description: "Comprehensive martial arts training combining traditional techniques.",
            age_range: "13+ years",
            features: ["Advanced techniques", "Physical fitness", "Mental discipline", "Real-world application"],
            image_url: "https://d598mmm7zleni.cloudfront.net/images/adults.png"
          },
          {
            id: 4,
            title: "Women's Self-Defense (CAT)",
            description: "Certified CAT self-defense program for women's safety.",
            age_range: "Women only",
            features: ["CAT certified", "Women-focused", "Practical techniques", "Empowerment"],
            image_url: "https://d598mmm7zleni.cloudfront.net/images/womens.jpg"
          }
        ];
      }
    });
  },

  // Testimonials
  async getTestimonials() {
    return cachedFetch('testimonials', async () => {
      try {
        const response = await fetch(`${API}/testimonials`);
        if (response.ok) {
          return await response.json();
        }
        throw new Error('Failed to fetch testimonials');
      } catch (error) {
        console.error('Error fetching testimonials:', error);
        // Fallback to mock data
        return [
          {
            id: 1,
            name: "Sarah Johnson",
            rating: 5,
            text: "Owaza has transformed my daughter's confidence. The instructors are amazing and the appointment-only format means she gets the attention she needs.",
            program: "Youth Program"
          },
          {
            id: 2,
            name: "Mike Chen",
            rating: 5,
            text: "Best martial arts studio I've been to. The CAT system is incredibly practical and the adult background checks give me peace of mind.",
            program: "Adult Program"
          },
          {
            id: 3,
            name: "Lisa Martinez",
            rating: 5,
            text: "The women's self-defense program has been life-changing. I feel so much more confident and prepared now.",
            program: "Women's Self-Defense"
          },
          {
            id: 4,
            name: "David Thompson",
            rating: 5,
            text: "Professional, focused, and effective training. The appointment system eliminates all distractions.",
            program: "Adult Program"
          },
          {
            id: 5,
            name: "Jennifer Lee",
            rating: 5,
            text: "My son loves his classes here. The instructors are patient and skilled at working with kids.",
            program: "Kids Program"
          },
          {
            id: 6,
            name: "Robert Wilson",
            rating: 5,
            text: "Excellent martial arts instruction with a focus on practical application. Highly recommend!",
            program: "Adult Program"
          }
        ];
      }
    });
  },

  // Hero Content
  async getHeroContent() {
    return cachedFetch('heroContent', async () => {
      try {
        const response = await fetch(`${API}/hero-content`);
        if (response.ok) {
          return await response.json();
        }
        throw new Error('Failed to fetch hero content');
      } catch (error) {
        console.error('Error fetching hero content:', error);
        // Fallback to default
        return {
          title_line1: "Master Your",
          title_line2: "Inner Warrior",
          subtitle: "Expert martial arts training in Spring Hill, FL. Certified CAT self-defense • Kids to Adults • Results-driven approach",
          hero_image_url: "https://d598mmm7zleni.cloudfront.net/images/hero.png"
        };
      }
    });
  },

  // Navigation Links
  async getNavigationLinks(nested = false) {
    const cacheKey = nested ? 'navigationLinks:nested' : 'navigationLinks';
    return cachedFetch(cacheKey, async () => {
      try {
        const url = nested
          ? `${API}/navigation-links?nested=true`
          : `${API}/navigation-links`;
        const response = await fetch(url);
        if (response.ok) {
          return await response.json();
        }
        throw new Error('Failed to fetch navigation links');
      } catch (error) {
        console.error('Error fetching navigation links:', error);
        return [];
      }
    });
  },

  // Schedule
  async getSchedule() {
    return cachedFetch('schedule', async () => {
      try {
        const response = await fetch(`${API}/schedule`);
        if (response.ok) {
          return await response.json();
        }
        throw new Error('Failed to fetch schedule');
      } catch (error) {
        console.error('Error fetching schedule:', error);
        return [];
      }
    });
  }
};